import React, { useEffect, useMemo, useRef } from 'react'
import classNames from 'classnames'
import { Controller, useForm } from 'react-hook-form'
import { useAppSelector } from '../../../../store/hooks'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { useUpdateAccountMutation } from '../../../../store/accountUserSettings/accountUserSettings'
import { useGetAllUsersQuery } from '../../../../store/user/user'
import { type Account } from '../../../../models/Account'
import { getUserDisplayName } from '../../../../apps/professional-app/fa/common/aifeatures/utils'
import { MultiSelectDropdown, type MultiSelectOption } from '../../../../components/MultiSelectDropdown/MultiSelectDropdown'
import { Button } from '../../../../components/Button/Button'
import { ScopedBlock } from '../../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../../components/ScopedBlock/models'

interface FormValues {
  allowedUserIds: string[]
}

export interface ClientQueryUserAccessTableProps {
  account: Account
}

export const ClientQueryUserAccessTable: React.FC<ClientQueryUserAccessTableProps> = ({ account }) => {
  const accountId = useAppSelector(selectAccountId)
  const { data: usersData, isFetching: isUsersLoading } = useGetAllUsersQuery({ accountId, identityTypes: ['idp'] }, { refetchOnMountOrArgChange: true })
  const [updateAccount, { isLoading: accountUpdateLoading }] = useUpdateAccountMutation()

  const savedUserIds = useMemo(() => account.config.crossClientQueryAiPhaseAllowedUserIds ?? [], [account.config.crossClientQueryAiPhaseAllowedUserIds])

  const { control, handleSubmit, reset, formState } = useForm<FormValues>({
    defaultValues: { allowedUserIds: savedUserIds },
  })

  const userOptions: MultiSelectOption[] = useMemo(
    () =>
      [...(usersData?.data ?? [])]
        .sort((a, b) => getUserDisplayName(a).localeCompare(getUserDisplayName(b)))
        .map((user) => ({ id: user.id, label: getUserDisplayName(user) })),
    [usersData],
  )

  // Drop ids for users no longer on the account (e.g. removed since the last save) so a stale grant
  // isn't silently re-persisted on the next Save, and the selection count reflects only real users.
  // This only prunes the local form; account.config itself may still carry the stale id until this
  // widget's own Save runs, so AccountSettings' own submit handlers sanitize it before every save
  // of theirs too (see sanitizeAccountConfig there) rather than this component racing to persist it.
  // Runs once per usersData load: comparing against the (static) savedUserIds prop on every render
  // would never converge, since the prop itself doesn't shrink after reset() prunes the form value.
  const hasPrunedStaleUserIds = useRef(false)
  useEffect(() => {
    if (!usersData || isUsersLoading || hasPrunedStaleUserIds.current) {
      return
    }
    hasPrunedStaleUserIds.current = true
    const validUserIds = new Set(userOptions.map((option) => option.id))
    const filteredUserIds = savedUserIds.filter((userId) => validUserIds.has(userId))
    if (filteredUserIds.length !== savedUserIds.length) {
      reset({ allowedUserIds: filteredUserIds })
    }
  }, [usersData, isUsersLoading, userOptions, savedUserIds, reset])

  const onSubmit = async (data: FormValues) => {
    const validUserIds = new Set(userOptions.map((option) => option.id))
    const allowedUserIds = data.allowedUserIds.filter((userId) => validUserIds.has(userId))
    const result = await updateAccount({
      accountId,
      payload: {
        config: { ...account.config, crossClientQueryAiPhaseAllowedUserIds: allowedUserIds },
      },
    })
    if ('data' in result && result.data) {
      reset({ allowedUserIds: result.data.data.config.crossClientQueryAiPhaseAllowedUserIds ?? [] })
    }
  }

  // Revert to react-hook-form's own tracked default, not the raw savedUserIds prop: the latter never
  // reflects the stale-id prune above, so resetting to it directly would resurrect a pruned id.
  const handleCancel = () => {
    reset()
  }

  return (
    <div className='d-flex flex-column gap-2'>
      <div className='paragraph-medium medium'>Who can use it</div>
      <Controller
        name='allowedUserIds'
        control={control}
        render={({ field }) => (
          <ScopedBlock scope={{ scope: 'account:update', superScope: ['account:admin'] }} missingScopeAction={MissingScopeAction.DISABLE}>
            <MultiSelectDropdown
              name={field.name}
              options={userOptions}
              initialSelectedOptions={field.value}
              onSelectedChanged={field.onChange}
              placeholder='Select users…'
              showSelectAllOption
              disabled={isUsersLoading || accountUpdateLoading}
              loading={isUsersLoading}
            />
          </ScopedBlock>
        )}
      />
      <ScopedBlock scope={{ scope: 'account:update', superScope: ['account:admin'] }} missingScopeAction={MissingScopeAction.DISABLE}>
        <div className='d-flex zocks-gap-0.625'>
          <Button variant='primary' onClick={handleSubmit(onSubmit)} loading={accountUpdateLoading} disabled={accountUpdateLoading || !formState.isDirty}>
            Save changes
          </Button>
          <Button
            variant='borderless'
            className={classNames({ 'zocks-text-primary': !accountUpdateLoading && formState.isDirty })}
            onClick={handleCancel}
            disabled={accountUpdateLoading || !formState.isDirty}
          >
            Cancel
          </Button>
        </div>
      </ScopedBlock>
    </div>
  )
}
